// ─── Demo Mode Data ────────────────────────────────────────────────────────────
// Static fixtures + tick simulation for the demo/guest flow.
// Nothing here makes real API calls.

import { MarketTick } from "@/providers/ws-provider"

// ─── Fake instrument tokens (prefixed 99xxx to avoid clashes) ─────────────────
export const DEMO_TOKENS: Record<string, number> = {
  GOLD24FEBFUT: 99001,
  CRUDEOIL24FEBFUT: 99002,
  SILVER24MARFUT: 99003,
  NATURALGAS24FEBFUT: 99004,
  COPPER24FEBFUT: 99005,
  NIFTY24FEBFUT: 99006,
  BANKNIFTY24FEBFUT: 99007,
  "NIFTY2402122200CE": 99008,
  "NIFTY2402122000PE": 99009,
  RELIANCE24FEBFUT: 99010,
  // BSE Index F&O — Index F&O only (decision #2)
  SENSEX24FEBFUT: 99011,
  BANKEX24FEBFUT: 99012,
  "SENSEX2402128000CE": 99013,
}

export const DEMO_SCRIP_TOKEN_MAP: Record<string, number> = { ...DEMO_TOKENS }

export const DEMO_WATCHLIST = Object.keys(DEMO_TOKENS)

export const DEMO_SCRIPS = [
  { id: 1, scrip_id: "GOLD24FEBFUT", name: "Gold Feb 2024 Fut", lotsize: 100, market_type: "MCX", zerodha_instrument_token: 99001, tick_size: 1, expiry_date: "2025-02-28", strike_price: 0, is_enabled: 1, status: 1 },
  { id: 2, scrip_id: "CRUDEOIL24FEBFUT", name: "Crude Oil Feb 2024 Fut", lotsize: 100, market_type: "MCX", zerodha_instrument_token: 99002, tick_size: 1, expiry_date: "2025-02-19", strike_price: 0, is_enabled: 1, status: 1 },
  { id: 3, scrip_id: "SILVER24MARFUT", name: "Silver Mar 2024 Fut", lotsize: 30, market_type: "MCX", zerodha_instrument_token: 99003, tick_size: 1, expiry_date: "2025-03-05", strike_price: 0, is_enabled: 1, status: 1 },
  { id: 4, scrip_id: "NATURALGAS24FEBFUT", name: "Natural Gas Feb 2024 Fut", lotsize: 1250, market_type: "MCX", zerodha_instrument_token: 99004, tick_size: 0.1, expiry_date: "2025-02-26", strike_price: 0, is_enabled: 1, status: 1 },
  { id: 5, scrip_id: "COPPER24FEBFUT", name: "Copper Feb 2024 Fut", lotsize: 2500, market_type: "MCX", zerodha_instrument_token: 99005, tick_size: 0.05, expiry_date: "2025-02-28", strike_price: 0, is_enabled: 1, status: 1 },
  { id: 6, scrip_id: "NIFTY24FEBFUT", name: "Nifty Feb 2024 Fut", lotsize: 50, market_type: "NSE", zerodha_instrument_token: 99006, tick_size: 0.05, expiry_date: "2025-02-27", strike_price: 0, is_enabled: 1, status: 1 },
  { id: 7, scrip_id: "BANKNIFTY24FEBFUT", name: "Bank Nifty Feb 2024 Fut", lotsize: 15, market_type: "NSE", zerodha_instrument_token: 99007, tick_size: 0.05, expiry_date: "2025-02-26", strike_price: 0, is_enabled: 1, status: 1 },
  { id: 8, scrip_id: "NIFTY2402122200CE", name: "Nifty 22200 CE Feb 2024", lotsize: 50, market_type: "NSE", zerodha_instrument_token: 99008, tick_size: 0.05, expiry_date: "2025-02-12", strike_price: 22200, is_enabled: 1, status: 1 },
  { id: 9, scrip_id: "NIFTY2402122000PE", name: "Nifty 22000 PE Feb 2024", lotsize: 50, market_type: "NSE", zerodha_instrument_token: 99009, tick_size: 0.05, expiry_date: "2025-02-12", strike_price: 22000, is_enabled: 1, status: 1 },
  { id: 10, scrip_id: "RELIANCE24FEBFUT", name: "Reliance Feb 2024 Fut", lotsize: 250, market_type: "NSE", zerodha_instrument_token: 99010, tick_size: 0.05, expiry_date: "2025-02-27", strike_price: 0, is_enabled: 1, status: 1 },
  // BSE Index F&O demo scrips
  { id: 11, scrip_id: "SENSEX24FEBFUT", name: "Sensex Feb 2024 Fut", lotsize: 10, market_type: "BSE", zerodha_instrument_token: 99011, tick_size: 0.05, expiry_date: "2025-02-28", strike_price: 0, is_enabled: 1, status: 1 },
  { id: 12, scrip_id: "BANKEX24FEBFUT", name: "Bankex Feb 2024 Fut", lotsize: 15, market_type: "BSE", zerodha_instrument_token: 99012, tick_size: 0.05, expiry_date: "2025-02-26", strike_price: 0, is_enabled: 1, status: 1 },
  { id: 13, scrip_id: "SENSEX2402128000CE", name: "Sensex 80000 CE Feb 2024", lotsize: 10, market_type: "BSE", zerodha_instrument_token: 99013, tick_size: 0.05, expiry_date: "2025-02-12", strike_price: 80000, is_enabled: 1, status: 1 },
]

// ─── Price anchors per token ───────────────────────────────────────────────────
interface PriceAnchor {
  ltp: number; prevClose: number
  open: number; high: number; low: number
  volume: number; atp: number; oi: number
}

const ANCHORS: Record<number, PriceAnchor> = {
  99001: { ltp: 62450, prevClose: 62100, open: 62200, high: 62600, low: 62050, volume: 12450, atp: 62310, oi: 8720 },
  99002: { ltp: 6185, prevClose: 6250, open: 6230, high: 6280, low: 6160, volume: 32100, atp: 6218, oi: 45200 },
  99003: { ltp: 73200, prevClose: 72800, open: 72900, high: 73400, low: 72700, volume: 4200, atp: 73050, oi: 2100 },
  99004: { ltp: 263.5, prevClose: 271.0, open: 269.0, high: 271.5, low: 261.0, volume: 98200, atp: 265.8, oi: 62400 },
  99005: { ltp: 738.5, prevClose: 732.0, open: 733.0, high: 741.0, low: 731.5, volume: 7800, atp: 736.2, oi: 5100 },
  99006: { ltp: 21982, prevClose: 21850, open: 21870, high: 22050, low: 21820, volume: 89500, atp: 21940, oi: 134500 },
  99007: { ltp: 46520, prevClose: 46800, open: 46750, high: 46900, low: 46410, volume: 54200, atp: 46630, oi: 87600 },
  99008: { ltp: 245, prevClose: 210, open: 215, high: 265, low: 195, volume: 125000, atp: 238, oi: 654000 },
  99009: { ltp: 125, prevClose: 165, open: 158, high: 162, low: 118, volume: 98000, atp: 138, oi: 521000 },
  99010: { ltp: 2945, prevClose: 2890, open: 2895, high: 2960, low: 2882, volume: 18500, atp: 2921, oi: 34200 },
  // BSE Index F&O anchors
  99011: { ltp: 81250, prevClose: 80800, open: 80900, high: 81400, low: 80750, volume: 18400, atp: 81100, oi: 24500 },
  99012: { ltp: 52480, prevClose: 52100, open: 52150, high: 52600, low: 52050, volume: 9800, atp: 52340, oi: 14200 },
  99013: { ltp: 1320, prevClose: 1150, open: 1200, high: 1380, low: 1080, volume: 42000, atp: 1280, oi: 184000 },
}

// ─── Build initial tick snapshot ───────────────────────────────────────────────
export function buildInitialDemoTicks(): Record<number, MarketTick> {
  const ticks: Record<number, MarketTick> = {}
  for (const [scripId, token] of Object.entries(DEMO_TOKENS)) {
    const a = ANCHORS[token]
    const spread = a.ltp * 0.0003
    const change = parseFloat(((a.ltp - a.prevClose) / a.prevClose * 100).toFixed(2))
    ticks[token] = {
      symbol: scripId, token,
      ltp: a.ltp,
      bid: parseFloat((a.ltp - spread).toFixed(2)),
      ask: parseFloat((a.ltp + spread).toFixed(2)),
      open: a.open, high: a.high, low: a.low, prevClose: a.prevClose,
      volume: a.volume, atp: a.atp, oi: a.oi,
      time: Date.now() / 1000,
      change,
      lastTradedQty: Math.floor(Math.random() * 5) + 1,
      bidQty: Math.floor(Math.random() * 80) + 20,
      askQty: Math.floor(Math.random() * 80) + 20,
      lowerCircuit: parseFloat((a.prevClose * 0.9).toFixed(2)),
      upperCircuit: parseFloat((a.prevClose * 1.1).toFixed(2)),
    }
  }
  return ticks
}

// ─── Simulate a tick update (±0.15% random walk per interval) ─────────────────
export function fluctuateDemoTicks(current: Record<number, MarketTick>): Record<number, MarketTick> {
  const next: Record<number, MarketTick> = {}
  for (const [tokenStr, tick] of Object.entries(current)) {
    const token = Number(tokenStr)
    const a = ANCHORS[token]
    if (!a) { next[token] = tick; continue }
    const maxMove = a.ltp * 0.0015
    const delta = (Math.random() - 0.5) * 2 * maxMove
    const newLtp = parseFloat(Math.max(a.low * 0.97, Math.min(a.high * 1.03, tick.ltp + delta)).toFixed(2))
    const spread = newLtp * 0.0003
    const change = parseFloat(((newLtp - a.prevClose) / a.prevClose * 100).toFixed(2))
    next[token] = {
      ...tick,
      ltp: newLtp,
      bid: parseFloat((newLtp - spread).toFixed(2)),
      ask: parseFloat((newLtp + spread).toFixed(2)),
      change,
      time: Date.now() / 1000,
      lastTradedQty: Math.floor(Math.random() * 5) + 1,
      bidQty: Math.floor(Math.random() * 100) + 20,
      askQty: Math.floor(Math.random() * 100) + 20,
      volume: tick.volume + Math.floor(Math.random() * 8),
    }
  }
  return next
}

// ─── Demo orders ───────────────────────────────────────────────────────────────
const now = Date.now()
const ago = (m: number) => new Date(now - m * 60 * 1000).toISOString()

export const DEMO_PENDING_ORDERS = [
  {
    id: 90001, scrip_id: "GOLD24FEBFUT", market_type: "MCX",
    side: "BUY", order_type: "Buy", lots: 2, balance_lots: 2, lot_size: 100,
    scheduled_price: 62300, trigger_price: 62280, price_per_share: 62300,
    validity: "DAY", product: "NRML", trade_type: "Future",
    status: "OPEN", status_code: 1,
    order_id: "DEMO90001", exchange_order_id: "DEMO90001",
    order_time: ago(12), created_at: ago(12), time: ago(12), margin_required: 124600,
    userid: 201, username: "trader_joe", condition: ">=",
  },
  {
    id: 90002, scrip_id: "NIFTY24FEBFUT", market_type: "NSE",
    side: "SELL", order_type: "Sell", lots: 1, balance_lots: 1, lot_size: 50,
    scheduled_price: 22100, trigger_price: 22150, price_per_share: 22100,
    validity: "DAY", product: "NRML", trade_type: "Future",
    status: "OPEN", status_code: 1,
    order_id: "DEMO90002", exchange_order_id: "DEMO90002",
    order_time: ago(6), created_at: ago(6), time: ago(6), margin_required: 110050,
    userid: 202, username: "alpha_trader", condition: "<=",
  },
  {
    id: 90003, scrip_id: "CRUDEOIL24FEBFUT", market_type: "MCX",
    side: "BUY", order_type: "Buy", lots: 3, balance_lots: 3, lot_size: 100,
    scheduled_price: 6180, trigger_price: 6175, price_per_share: 6180,
    validity: "DAY", product: "NRML", trade_type: "Future",
    status: "OPEN", status_code: 1,
    order_id: "DEMO90003", exchange_order_id: "DEMO90003",
    order_time: ago(2), created_at: ago(2), time: ago(2), margin_required: 185550,
    userid: 203, username: "silver_fox", condition: ">=",
  },
]

export const DEMO_HISTORY_ORDERS = [
  {
    id: 80001, scrip_id: "GOLD24FEBFUT", market_type: "MCX",
    side: "BUY", txn_type: 1, lots: 1, lot_size: 100,
    executed_price: 62100, avg_price: 62100, price_per_share: 62100, scheduled_price: 62100,
    status: "executed", category: "EXECUTED",
    order_id: "DEMO80001", exchange_order_id: "DEMO80001",
    execution_time: ago(90), order_time: ago(92), trade_type: "Future",
  },
  {
    id: 80002, scrip_id: "GOLD24FEBFUT", market_type: "MCX",
    side: "SELL", txn_type: 2, lots: 1, lot_size: 100,
    executed_price: 63200, avg_price: 63200, price_per_share: 63200, scheduled_price: 63200,
    status: "executed", category: "EXECUTED",
    order_id: "DEMO80002", exchange_order_id: "DEMO80002",
    execution_time: ago(45), order_time: ago(47), trade_type: "Future",
  },
  {
    id: 80003, scrip_id: "NIFTY24FEBFUT", market_type: "NSE",
    side: "BUY", txn_type: 1, lots: 2, lot_size: 50,
    executed_price: 21500, avg_price: 21500, price_per_share: 21500, scheduled_price: 21500,
    status: "executed", category: "EXECUTED",
    order_id: "DEMO80003", exchange_order_id: "DEMO80003",
    execution_time: ago(180), order_time: ago(182), trade_type: "Future",
  },
  {
    id: 80004, scrip_id: "NIFTY24FEBFUT", market_type: "NSE",
    side: "SELL", txn_type: 2, lots: 2, lot_size: 50,
    executed_price: 21200, avg_price: 21200, price_per_share: 21200, scheduled_price: 21200,
    status: "executed", category: "EXECUTED",
    order_id: "DEMO80004", exchange_order_id: "DEMO80004",
    execution_time: ago(120), order_time: ago(122), trade_type: "Future",
  },
  {
    id: 80005, scrip_id: "SILVER24MARFUT", market_type: "MCX",
    side: "BUY", txn_type: 1, lots: 1, lot_size: 30,
    scheduled_price: 72000, status: "cancelled", category: "CANCELLED",
    order_id: "DEMO80005", exchange_order_id: "DEMO80005",
    order_time: ago(200), trade_type: "Future",
  },
  {
    id: 80006, scrip_id: "CRUDEOIL24FEBFUT", market_type: "MCX",
    side: "SELL", txn_type: 2, lots: 2, lot_size: 100,
    scheduled_price: 6300, status: "rejected", category: "REJECTED",
    order_id: "DEMO80006", exchange_order_id: "DEMO80006",
    order_time: ago(250), trade_type: "Future",
  },
  {
    id: 80007, scrip_id: "BANKNIFTY24FEBFUT", market_type: "NSE",
    side: "BUY", txn_type: 1, lots: 1, lot_size: 15,
    executed_price: 46200, avg_price: 46200, price_per_share: 46200, scheduled_price: 46200,
    status: "executed", category: "EXECUTED",
    order_id: "DEMO80007", exchange_order_id: "DEMO80007",
    execution_time: ago(300), order_time: ago(302), trade_type: "Future",
  },
]

// ─── Demo positions ────────────────────────────────────────────────────────────
export const DEMO_POSITIONS = [
  {
    id: 70001, scrip_id: "CRUDEOIL24FEBFUT", market_type: "MCX",
    net_lots: 1, active_buy_lots: 1, active_sell_lots: 0,
    avg_buy_rate: "6250", avg_sell_rate: "6240",
    lot_size: 100, trade_qty_type: "Lot" as const,
    m2m: "-6500", ltp: 6185, margin_required: 61850, holding_margin: 25000, is_held: 0,
    trade_type: "",
  },
  {
    id: 70002, scrip_id: "SILVER24MARFUT", market_type: "MCX",
    net_lots: 2, active_buy_lots: 2, active_sell_lots: 0,
    avg_buy_rate: "72500", avg_sell_rate: "72450",
    lot_size: 30, trade_qty_type: "Lot" as const,
    m2m: "42000", ltp: 73200, margin_required: 43920, holding_margin: 35000, is_held: 0,
    trade_type: "",
  },
  {
    id: 70003, scrip_id: "NIFTY24FEBFUT", market_type: "NSE",
    net_lots: -1, active_buy_lots: 0, active_sell_lots: 1,
    avg_buy_rate: "22050", avg_sell_rate: "22100",
    lot_size: 50, trade_qty_type: "Lot" as const,
    m2m: "5900", ltp: 21982, margin_required: 109910, holding_margin: 50000, is_held: 0,
    trade_type: "",
  },
  {
    id: 70004, scrip_id: "BANKNIFTY24FEBFUT", market_type: "NSE",
    net_lots: 1, active_buy_lots: 1, active_sell_lots: 0,
    avg_buy_rate: "46800", avg_sell_rate: "46750",
    lot_size: 15, trade_qty_type: "Lot" as const,
    m2m: "-4200", ltp: 46520, margin_required: 69780, holding_margin: 30000, is_held: 0,
    trade_type: "",
  },
]

// ─── Demo closed positions ─────────────────────────────────────────────────────
export const DEMO_CLOSED_POSITIONS = [
  {
    scrip_id: "GOLD24FEBFUT",
    lots: 1,
    lot_size: 100,
    avg_buy_rate: 62100,
    avg_sell_rate: 63200,
    realized_pnl: 110000,
    market_type: "MCX",
    trade_type: "Future",
    closed_at: ago(45),
  },
  {
    scrip_id: "NIFTY24FEBFUT",
    lots: 2,
    lot_size: 50,
    avg_buy_rate: 21500,
    avg_sell_rate: 21200,
    realized_pnl: -30000,
    market_type: "NSE",
    trade_type: "Future",
    closed_at: ago(120),
  },
  {
    scrip_id: "BANKNIFTY24FEBFUT",
    lots: 1,
    lot_size: 15,
    avg_buy_rate: 46200,
    avg_sell_rate: 46800,
    realized_pnl: 9000,
    market_type: "NSE",
    trade_type: "Future",
    closed_at: ago(300),
  },
]

// ─── Demo funds ────────────────────────────────────────────────────────────────
export const DEMO_FUNDS = {
  balance: 200000,
  used_margin: 285460,
  available_margin: 200000,
  pnl: 110000,
  active_pnl: -1700,
  m2m_balance: -1700,
}

export const DEMO_TRANSACTIONS = [
  { id: 1, type: "CREDIT", amount: 500000, description: "Initial deposit", created_at: "2024-01-15T10:00:00" },
  { id: 2, type: "DEBIT", amount: 15000, description: "Brokerage charges Jan", created_at: "2024-02-01T14:30:00" },
  { id: 3, type: "CREDIT", amount: 110000, description: "Profit — GOLD trade", created_at: "2024-02-14T16:00:00" },
  { id: 4, type: "DEBIT", amount: 395000, description: "Withdrawal to bank", created_at: "2024-02-20T09:00:00" },
]

// ─── Demo notifications ────────────────────────────────────────────────────────
const sec = (s: number) => Date.now() / 1000 - s
export const DEMO_NOTIFICATIONS = [
  { id: 1, title: "Order Executed", message: "GOLD24FEBFUT BUY 1 lot @ ₹62,100 executed successfully.", time: sec(5400), is_read: false },
  { id: 2, title: "Margin Alert", message: "Your used margin has crossed 60% of your available balance.", time: sec(3600), is_read: false },
  { id: 3, title: "Order Executed", message: "NIFTY24FEBFUT BUY 2 lots @ ₹21,500 executed successfully.", time: sec(10800), is_read: true },
  { id: 4, title: "Order Cancelled", message: "SILVER24MARFUT BUY order cancelled as per your request.", time: sec(12000), is_read: true },
  { id: 5, title: "Auto Square-Off Warning", message: "CRUDEOIL24FEBFUT position is approaching auto square-off limit.", time: sec(18000), is_read: true },
]

// ─── Demo profile details ─────────────────────────────────────────────────────
export const DEMO_PROFILE_DETAILS = {
  success: 1,
  mcx_enabled: "Enabled",
  nse_enabled: "Enabled",
  bse_enabled: "Enabled",
  nse_stock_enabled: "Enabled",
  nse_options_enabled: "Enabled",
  bse_options_enabled: "Enabled",
  mcx_brokerage_type: "per_lot",
  nse_brokerage_type: "per_crore",
  options_brokerage_type: "per_lot",
  nse_stock_brokerage_type: "per_crore",
  nse_brokerage: "250",
  mcx_brokerage: "120",
  options_brokerage: "20",
  nse_stock_brokerage: "200",
  mcx_exposure_type: "per_lot",
  nse_exposure_type: "per_turnover",
  options_exposure_type: "per_lot",
  nse_stock_exposure_type: "per_turnover",
  nse_intraday_margin: 1000,
  mcx_intraday_margin: 2000,
  nse_holding_margin: 500,
  mcx_holding_margin: 1000,
  options_intraday_margin: 500,
  options_holding_margin: 250,
  nse_stock_intraday_margin: 5,
  nse_stock_holding_margin: 1,
  auto_square_off_percentage: 80,
  notify_percentage: 60,
  ledger_balance: 200000,
  available_balance: 200000,
  m2m_balance: -1700,
  username: "DEMO001",
  is_demo: 1,
  nse_bid_gap_percentage: 0.05,
  mcx_per_lot_margins: [
    { trade_name: "GOLD", intraday: "7500", holding: "15000" },
    { trade_name: "CRUDEOIL", intraday: "5000", holding: "10000" },
    { trade_name: "SILVER", intraday: "4500", holding: "9000" },
    { trade_name: "NATURALGAS", intraday: "900", holding: "1800" },
    { trade_name: "COPPER", intraday: "3000", holding: "6000" },
  ],
  mcx_per_lot_brokerages: [
    { trade_name: "GOLD", amount: "120" },
    { trade_name: "CRUDEOIL", amount: "80" },
    { trade_name: "SILVER", amount: "100" },
    { trade_name: "NATURALGAS", amount: "30" },
    { trade_name: "COPPER", amount: "60" },
  ],
  mcx_specific_bid_gaps: [] as { trade_name: string; amount: string }[],
  mcx_lot_brokerage_json: {} as Record<string, string>,
  options_lot_brokerage_json: { NIFTY: "20", BANKNIFTY: "20" } as Record<string, string>,
  mcx_bid_gap_json: {} as Record<string, string>,
}

// ─── Demo profile ──────────────────────────────────────────────────────────────
export const DEMO_PROFILE = {
  id: 99999,
  username: "DEMO001",
  name: "Demo Trader",
  mobile: "+91 99999 00000",
  broker_name: process.env.NEXT_PUBLIC_APP_NAME ?? "App-FX",
  user_type: "TRADER",
  status: 1,
  balance: 200000,
  mcx_enabled: 1 as 0 | 1,
  nse_enabled: 1 as 0 | 1,
  bse_enabled: 1 as 0 | 1,
  nse_stock_enabled: 1 as 0 | 1,
  nse_options_enabled: 1 as 0 | 1,
  bse_options_enabled: 1 as 0 | 1,
  nse_trade_qty_type: "Lot",
  mcx_trade_qty_type: "Lot",
  nse_options_trade_qty_type: "Lot",
  nse_stock_trade_qty_type: "Quantity",
  is_first_login: false,
  show_fund_buttons: true,
}
